import type { StatusTone } from "../components/ds/model";
import type { MessageKey } from "./i18n/locales/en";
import type { HighwayNotStarted, HighwayRun, HighwayRunSummary, HighwayRunsResponse } from "./rfqApi";

/** Engine highway steps (rfq/highway.py STEPS), in run order. */
export const AGENT_STEPS = [
  "intake",
  "geometry",
  "triage",
  "stock",
  "make_buy",
  "supplier",
  "cost",
  "audit",
  "margin",
  "send",
] as const;

export type AgentStep = (typeof AGENT_STEPS)[number];

/** Steps that can stop a run for a person. The rest never stop. */
const STEP_STOP: Partial<Record<AgentStep, MessageKey>> = {
  intake: "agents.step.intake.stop",
  geometry: "agents.step.geometry.stop",
  triage: "agents.step.triage.stop",
  supplier: "agents.step.supplier.stop",
  audit: "agents.step.audit.stop",
  margin: "agents.step.margin.stop",
  send: "agents.step.send.stop",
};

export function stepKeys(step: AgentStep): { name: MessageKey; does: MessageKey; stop: MessageKey } {
  return {
    name: `agents.step.${step}.name` as MessageKey,
    does: `agents.step.${step}.does` as MessageKey,
    stop: STEP_STOP[step] ?? "agents.noStop",
  };
}

const GATE_KEY: Record<string, MessageKey> = {
  intake_pending: "agents.gate.intake_pending",
  parse_pending: "agents.gate.parse_pending",
  ocr_unconfirmed: "agents.gate.ocr_unconfirmed",
  intake_blocker: "agents.gate.intake_blocker",
  no_lines: "agents.gate.no_lines",
  parse_failed: "agents.gate.parse_failed",
  geometry_uncertain: "agents.gate.geometry_uncertain",
  customer_question: "agents.gate.customer_question",
  supplier_reply: "agents.gate.supplier_reply",
  audit_blocker: "agents.gate.audit_blocker",
  margin_approval: "agents.gate.margin_approval",
  quote_send: "agents.gate.quote_send",
};

/** What the person has to do at this gate. Unknown codes say "open the RFQ". */
export function gateKey(gate: string | null): MessageKey {
  return (gate && GATE_KEY[gate]) || "agents.gate.unknown";
}

/** Something is wrong (coral) versus the normal next step (amber). */
const BLOCKER_GATES = new Set(["intake_blocker", "parse_failed", "geometry_uncertain", "audit_blocker"]);

export function gateTone(gate: string | null): StatusTone {
  return gate && BLOCKER_GATES.has(gate) ? "coral" : "amber";
}

export type AgentsBoard = {
  /** Waiting on a person, newest activity first (the engine's order). */
  needsYou: HighwayRunSummary[];
  /** Queued, running, or waiting on an intake / parse job. */
  working: HighwayRunSummary[];
  failed: HighwayRunSummary[];
};

export function splitRuns(items: readonly HighwayRunSummary[]): AgentsBoard {
  const board: AgentsBoard = { needsYou: [], working: [], failed: [] };
  for (const run of items) {
    if (run.status === "failed") board.failed.push(run);
    else if (run.status === "waiting" && run.waiting_for === "human") board.needsYou.push(run);
    else board.working.push(run);
  }
  return board;
}

/** Row title: the RFQ title, else its number. Null when the engine sent neither. */
export function rfqLabel(run: HighwayRunSummary): string | null {
  return run.rfq_title?.trim() || run.rfq_no?.trim() || null;
}

export function rfqMeta(run: HighwayRunSummary): string {
  return [run.customer_name?.trim(), run.rfq_title?.trim() ? run.rfq_no?.trim() : null]
    .filter(Boolean)
    .join(" · ");
}

/** Stepper position: the step the run stands on, else the first one not done. */
export function stepperIndex(run: Pick<HighwayRun, "current_step" | "steps">): number {
  const at = AGENT_STEPS.indexOf(run.current_step as AgentStep);
  if (at >= 0) return at;
  const open = AGENT_STEPS.findIndex((step) => run.steps.find((s) => s.step === step)?.status !== "done");
  return open >= 0 ? open : AGENT_STEPS.length - 1;
}

export function workingKey(run: HighwayRunSummary): MessageKey {
  if (run.status === "queued") return "agents.workingQueued";
  if (run.status === "waiting") return "agents.workingJob";
  return "agents.workingRun";
}

/**
 * RFQ journey sub-path (parseRfqStep / opensTriage) where the person acts on this gate.
 * Null opens the bare RFQ, which picks its own step.
 */
const GATE_STEP: Record<string, string> = {
  intake_pending: "understood",
  parse_pending: "understood",
  ocr_unconfirmed: "understood",
  intake_blocker: "understood",
  no_lines: "understood",
  parse_failed: "understood",
  geometry_uncertain: "understood",
  customer_question: "triage",
  supplier_reply: "supply",
  audit_blocker: "quote",
  margin_approval: "margin",
  quote_send: "send",
};

export function gateStep(gate: string | null): string | null {
  return (gate && GATE_STEP[gate]) || null;
}

/** After "run the agents": the RFQ leaves not_started and shows as a run in progress. */
export function markStarted(
  data: HighwayRunsResponse,
  rfqId: string,
  run: Pick<HighwayRun, "id" | "status" | "current_step" | "gate" | "waiting_for">,
  now: string,
): HighwayRunsResponse {
  const row = data.not_started?.find((r) => r.rfq_id === rfqId);
  if (!row) return data;
  const started: HighwayRunSummary = {
    id: run.id,
    rfq_id: row.rfq_id,
    rfq_no: row.rfq_no,
    rfq_title: row.rfq_title,
    customer_name: row.customer_name,
    rfq_status: row.rfq_status,
    status: run.status,
    trigger: "manual",
    current_step: run.current_step,
    gate: run.gate,
    gate_reason: null,
    error: null,
    waiting_for: run.waiting_for,
    current_agent: null,
    started_at: now,
    updated_at: now,
    finished_at: null,
  };
  return {
    ...data,
    items: [started, ...data.items],
    count: data.count + 1,
    not_started: data.not_started?.filter((r) => r.rfq_id !== rfqId),
  };
}

/** Row title for an RFQ that has not started: title, else number. */
export function notStartedLabel(row: HighwayNotStarted): string | null {
  return row.rfq_title?.trim() || row.rfq_no?.trim() || null;
}
